<template>
	<div v-if="shouldRender" class="col-md-6 col-sm-12 mb-4">
		<GlassCard class="p-2 h-100">
			<div class="d-flex flex-column gap-2">
				<div
					class="d-flex flex-column flex-lg-row align-items-lg-center justify-content-between gap-2">
					<h2 class="p-2 flex-grow-1 mb-0">Health Connect Calorie Balance</h2>
					<button
						class="btn btn-sm btn-outline-light ms-lg-auto"
						type="button"
						@click="refresh"
						:disabled="loading"
						v-if="hasLoaded && hasRequiredPermissions">
						<span
							v-if="loading"
							class="spinner-border spinner-border-sm"
							role="status"
							aria-hidden="true"></span>
						<span class="ms-1" v-if="loading">Refreshing…</span>
						<span v-else>Refresh</span>
					</button>
				</div>
				<p
					class="px-2 mb-0 small text-white"
					v-if="hasLoaded && hasRequiredPermissions">
					Showing the last 30 days ({{ rangeLabel }}). Calories in are meals
					logged in Health Connect; calories out uses Health Connect total
					calories burned entries when permission is granted.
				</p>
			</div>
			<div class="p-2" v-if="error">
				<div class="alert alert-warning" role="alert">
					{{ error }}
				</div>
			</div>
			<div class="p-2" v-else-if="loading && !hasLoaded">
				<div class="d-flex align-items-center gap-2">
					<span
						class="spinner-border spinner-border-sm"
						role="status"
						aria-hidden="true"></span>
					<span>Loading Health Connect calories…</span>
				</div>
			</div>
			<div class="p-2" v-else-if="!hasRequiredPermissions">
				<p class="mb-2 text-white">
					Turn on the Health Connect "Read nutrition" and "Read total calories
					burned" permissions to enable this chart.
				</p>
				<button
					class="btn btn-outline-light btn-sm w-100"
					type="button"
					@click="openHealthConnectPermissions">
					Open Health Connect
				</button>
			</div>
			<div class="p-2" v-else-if="!hasChartData">
				<p class="mb-0 text-white">
					No Health Connect calorie data found in the last 30 days.
				</p>
			</div>
			<div class="p-2" v-else>
				<div class="summary mb-3">
					<div class="d-flex flex-wrap gap-3">
						<div class="summary-pill">
							<span class="label">Total in</span>
							<span class="value">{{ formattedTotalIn }} kcal</span>
						</div>
						<div class="summary-pill">
							<span class="label">Total out</span>
							<span class="value">{{ formattedTotalOut }} kcal</span>
						</div>
						<div class="summary-pill">
							<span class="label">Net</span>
							<span class="value" :class="netClass">
								{{ formattedNet }} kcal
							</span>
						</div>
					</div>
				</div>
				<div class="chart-area">
					<canvas ref="chartEl"></canvas>
				</div>
			</div>
		</GlassCard>
	</div>
</template>

<script setup>
	import { Capacitor } from "@capacitor/core"
	import { App } from "@capacitor/app"
	import Chart from "chart.js/auto"
	import moment from "moment-timezone"
	import { computed, nextTick, onBeforeUnmount, onMounted, ref } from "vue"
	import GlassCard from "@/components/GlassCard.vue"
	import { Health } from "@/plugins/Health"

	const shouldRender = ref(false)
	const hasNutritionPermission = ref(false)
	const hasCaloriesBurnedPermission = ref(false)
	const loading = ref(false)
	const error = ref("")
	const hasLoaded = ref(false)
	const chartEl = ref(null)
	const chartInstance = ref(null)
	let appStateListener
	const days = ref([])
	const totalIn = ref(0)
	const totalOut = ref(0)

	const timezone = moment.tz.guess()

	const hasChartData = computed(() => {
		return days.value.some((d) => d.caloriesIn > 0 || d.caloriesOut > 0)
	})

	const hasRequiredPermissions = computed(
		() => hasNutritionPermission.value && hasCaloriesBurnedPermission.value
	)

	const rangeLabel = computed(() => {
		if (!hasLoaded.value || days.value.length === 0) return ""
		const first = days.value[0]
		const last = days.value[days.value.length - 1]
		return `${first.label} - ${last.label}`
	})

	const formattedTotalIn = computed(() => Math.round(totalIn.value))
	const formattedTotalOut = computed(() => Math.round(totalOut.value))
	const netCalories = computed(() => totalIn.value - totalOut.value)
	const formattedNet = computed(() => Math.round(netCalories.value))
	const netClass = computed(() => {
		if (netCalories.value > 0) return "surplus"
		if (netCalories.value < 0) return "deficit"
		return ""
	})

	async function updatePermissionState() {
		try {
			const perms = await Health.getGrantedPermissions().catch(() => ({
				granted: [],
			}))
			const grantedList = Array.isArray(perms?.granted)
				? perms.granted.map((perm) => String(perm))
				: []
			hasNutritionPermission.value = grantedList.some((perm) =>
				perm.includes("READ_NUTRITION")
			)
			hasCaloriesBurnedPermission.value = grantedList.some((perm) =>
				perm.includes("READ_TOTAL_CALORIES_BURNED")
			)
		} catch (err) {
			hasNutritionPermission.value = false
			hasCaloriesBurnedPermission.value = false
			console.warn(
				"Failed to determine Health Connect calorie permissions",
				err
			)
		}
	}

	async function initialize() {
		if (
			!Capacitor.isNativePlatform() ||
			Capacitor.getPlatform() !== "android"
		) {
			return
		}

		try {
			const availability = await Health.isAvailable().catch(() => ({
				available: false,
			}))
			if (!availability?.available) {
				return
			}

			shouldRender.value = true
			await syncReport()
		} catch (err) {
			console.warn("Health Connect calorie report eligibility failed", err)
		}
	}

	function resetChart() {
		if (chartInstance.value) {
			chartInstance.value.destroy()
			chartInstance.value = null
		}
	}

	async function renderChart() {
		if (!chartEl.value) return
		const labels = days.value.map((d) => d.label)
		const realNetData = days.value.map((d) =>
			Number((d.caloriesIn - d.caloriesOut).toFixed(2))
		)
		const chartNetData = realNetData.map((v) => v)

		const surplusColor = "#ef4444"
		const surplusHover = "#f87171"
		const deficitColor = "#10b981"
		const deficitHover = "#34d399"

		const backgroundColors = realNetData.map((value) =>
			value >= 0 ? surplusColor : deficitColor
		)
		const hoverColors = realNetData.map((value) =>
			value >= 0 ? surplusHover : deficitHover
		)

		resetChart()

		const context = chartEl.value.getContext("2d")
		if (!context) return

		chartInstance.value = new Chart(context, {
			type: "bar",
			data: {
				labels,
				datasets: [
					{
						label: "Daily Net Calories",
						data: chartNetData,
						backgroundColor: backgroundColors,
						hoverBackgroundColor: hoverColors,
						borderColor: backgroundColors,
						hoverBorderColor: hoverColors,
						borderWidth: 1,
						borderSkipped: false,
						barPercentage: 0.85,
						categoryPercentage: 0.7,
						borderRadius: (ctx) => {
							const radius = 6
							const value = ctx?.parsed?.y ?? 0
							if (value >= 0) {
								return { topLeft: radius, topRight: radius }
							}
							return { bottomLeft: radius, bottomRight: radius }
						},
					},
				],
			},
			options: {
				responsive: true,
				maintainAspectRatio: false,
				animation: {
					duration: 320,
					easing: "easeOutQuart",
				},
				interaction: {
					intersect: false,
					mode: "index",
				},
				layout: {
					padding: {
						top: 12,
						right: 8,
						bottom: 0,
						left: 0,
					},
				},
				scales: {
					x: {
						grid: {
							display: false,
						},
						ticks: {
							color: "#ffffff",
							maxRotation: 0,
							minRotation: 0,
							autoSkipPadding: 16,
						},
					},
					y: {
						beginAtZero: true,
						grace: "15%",
						ticks: {
							color: "#ffffff",
							callback: (value) => `${value} kcal`,
						},
						grid: {
							color: (ctx) =>
								ctx.tick.value === 0
									? "rgba(255, 255, 255, 0.45)"
									: "rgba(255, 255, 255, 0.12)",
							lineWidth: (ctx) => (ctx.tick.value === 0 ? 1.5 : 1),
						},
					},
				},
				plugins: {
					legend: {
						display: false,
					},
					tooltip: {
						callbacks: {
							title: (items) => {
								if (!items?.length) return ""
								return items[0].label
							},
							label: (ctx) => {
								const day = days.value[ctx.dataIndex]
								const realNet = realNetData[ctx.dataIndex]
								if (!day) {
									return `${realNet.toFixed(0)} kcal net`
								}

								const inVal = Math.round(day.caloriesIn)
								const outVal = Math.round(day.caloriesOut)
								const direction = realNet >= 0 ? "surplus" : "deficit"
								return `Net ${Math.round(
									realNet
								)} kcal (${direction}). In: ${inVal} kcal, Out: ${outVal} kcal`
							},
						},
					},
				},
			},
		})
	}

	function buildEmptyDays() {
		const end = moment().tz(timezone).endOf("day")
		const start = end.clone().subtract(29, "days").startOf("day")
		const list = []
		for (let i = 0; i < 30; i += 1) {
			const day = start.clone().add(i, "days")
			list.push({
				key: day.format("YYYY-MM-DD"),
				label: day.format("MMM D"),
				caloriesIn: 0,
				caloriesOut: 0,
			})
		}
		return list
	}

	function findEntryForTimestamp(when, state) {
		if (!when) return null
		const m = moment(when)
		if (!m.isValid()) return null
		const key = m.tz(timezone).format("YYYY-MM-DD")
		return state.days.find((d) => d.key === key) ?? null
	}

	function parseKcal(value) {
		if (value === null || value === undefined) return null
		if (typeof value === "number") {
			return Number.isFinite(value) ? value : null
		}
		const numeric = Number(String(value).replace(/[^0-9.\-]/g, ""))
		return Number.isFinite(numeric) ? numeric : null
	}

	function applyNutritionRecord(record, state) {
		if (!record) return
		const timestamp =
			"caloriesIn" in record
				? record.startTime ?? record.endTime
				: record.endTime ?? record.startTime
		const entry = findEntryForTimestamp(timestamp, state)
		if (!entry) return

		const numeric = parseKcal(
			record.caloriesIn ?? record.calories ?? record.energy ?? null
		)
		if (numeric === null) return

		if (numeric >= 0) {
			entry.caloriesIn += numeric
			state.totalIn += numeric
		} else {
			const val = Math.abs(numeric)
			entry.caloriesOut += val
			state.totalOut += val
		}
	}

	function applyCaloriesBurnedRecord(record, state) {
		if (!record) return
		const timestamp =
			"caloriesOut" in record
				? record.startTime ?? record.endTime
				: record.endTime ?? record.startTime
		const entry = findEntryForTimestamp(timestamp, state)
		if (!entry) return

		const numeric = parseKcal(
			record.caloriesOut ?? record.energy ?? record.calories ?? null
		)
		if (numeric === null || numeric <= 0) return

		entry.caloriesOut += numeric
		state.totalOut += numeric
	}

	async function syncReport() {
		if (!shouldRender.value) return
		if (hasLoaded.value) {
			resetChart()
		}

		loading.value = true
		error.value = ""

		try {
			await updatePermissionState()

			if (!hasRequiredPermissions.value) {
				totalIn.value = 0
				totalOut.value = 0
				days.value = []
				hasLoaded.value = true
				return
			}

			const end = moment().tz(timezone).endOf("day")
			const start = end.clone().subtract(29, "days").startOf("day")
			const workingState = {
				days: buildEmptyDays(),
				totalIn: 0,
				totalOut: 0,
			}

			const nutritionResponse = await Health.aggregateDailyNutritionCalories({
				startTime: start.toISOString(),
				endTime: end.toISOString(),
			})
			const nutritionBuckets = Array.isArray(nutritionResponse?.buckets)
				? nutritionResponse.buckets
				: []

			for (const bucket of nutritionBuckets) {
				applyNutritionRecord(bucket, workingState)
			}

			if (hasCaloriesBurnedPermission.value) {
				try {
					const caloriesOutResponse = await Health.aggregateDailyCaloriesBurned(
						{
							startTime: start.toISOString(),
							endTime: end.toISOString(),
						}
					)
					const caloriesOutBuckets = Array.isArray(caloriesOutResponse?.buckets)
						? caloriesOutResponse.buckets
						: []

					for (const bucket of caloriesOutBuckets) {
						applyCaloriesBurnedRecord(bucket, workingState)
					}
				} catch (caloriesErr) {
					console.warn(
						"Failed to load Health Connect calories burned data",
						caloriesErr
					)
				}
			}

			days.value = workingState.days
			totalIn.value = workingState.totalIn
			totalOut.value = workingState.totalOut

			hasLoaded.value = true

			if (hasChartData.value) {
				await nextTick()
				await renderChart()
			} else {
				resetChart()
			}
		} catch (err) {
			console.warn("Failed to load Health Connect calorie data", err)
			error.value = "Unable to load Health Connect calorie data."
			resetChart()
		} finally {
			if (!hasRequiredPermissions.value) {
				resetChart()
			}
			loading.value = false
		}
	}

	async function openHealthConnectPermissions() {
		try {
			await Health.OpenHealthPermissionSettings()
		} catch (err) {
			console.warn("Failed to open Health Connect permissions", err)
			try {
				await Health.OpenGoogleHealthConnect()
			} catch (fallbackErr) {
				console.warn("Failed to open Health Connect app", fallbackErr)
			}
		}
	}

	async function refresh() {
		if (loading.value) return
		await syncReport()
	}

	onMounted(() => {
		void (async () => {
			try {
				appStateListener = await App.addListener('appStateChange', ({ isActive }) => {
					if (isActive) {
						void syncReport()
					}
				})
			} catch (err) {
				console.warn('Failed to register app state listener', err)
			}
		})()
		void initialize()
	})

	onBeforeUnmount(() => {
		if (appStateListener && typeof appStateListener.remove === 'function') {
			void appStateListener.remove()
			appStateListener = undefined
		}
		resetChart()
	})
</script>

<style scoped>
	.summary-pill {
		background: rgba(255, 255, 255, 0.08);
		border-radius: 12px;
		padding: 0.75rem 1rem;
		display: flex;
		flex-direction: column;
		min-width: 140px;
	}

	.summary-pill .label {
		font-size: 0.75rem;
		text-transform: uppercase;
		letter-spacing: 0.08em;
		color: rgba(255, 255, 255, 0.65);
	}

	.summary-pill .value {
		font-size: 1.15rem;
		font-weight: 600;
		color: #ffffff;
	}

	.summary-pill .value.surplus {
		color: #f87171;
	}

	.summary-pill .value.deficit {
		color: #34d399;
	}

	.chart-area {
		position: relative;
		min-height: 320px;
	}

	.chart-area canvas {
		width: 100% !important;
		height: 100% !important;
	}
</style>
